import type {
  RdScheduleAppointment,
  RdScheduleBlock,
  RdScheduleCalendarStatus,
  RdScheduleDay,
  RdScheduleHours,
  RdScheduleWeek,
} from "../../../shared/rd-schedule.js";
import { RD_SCHEDULE_FLAG } from "../../../shared/features.js";
import { MIRROR_FAILED_TEXT, MIRROR_PENDING_TEXT } from "./mirror-state.js";

// NO-874: the pure core of the Week view on My Appointments. Everything the
// grid, the key and the summary row print or place comes from here, so the
// components only lay it out. A leaf module with relative imports: `tsx --test`
// reads it (the `@/` alias does not resolve there).

/** Pixels per hour on the grid; the canvas's 56. */
export const WEEK_HOUR_PX = 56;
/** The grid shows at least these hours (8 AM to 6 PM on the canvas). */
export const WEEK_MIN_START_HOUR = 8;
export const WEEK_MIN_END_HOUR = 18;
/** Blocks shorter than this draw their tags on the first line. */
const TALL_BLOCK_PX = 40;

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
const QUARTERS: Readonly<Record<number, string>> = {
  0: "",
  1: "¼",
  2: "½",
  3: "¾",
};

// ---------------------------------------------------------------------------
// Request

export interface RdScheduleWeekRequest {
  /** The ISO Monday; null reads the current week. */
  week: string | null;
  timezone: string;
  rdId?: string;
}

/** Every Week read starts its key with this, so a write can refresh whichever weeks are cached. */
export const RD_SCHEDULE_WEEK_QUERY_PREFIX = ["rd-schedule-week"] as const;

/** The selection and the week belong in the key: each is a different read. */
export function rdScheduleWeekQueryKey(request: RdScheduleWeekRequest) {
  return [
    ...RD_SCHEDULE_WEEK_QUERY_PREFIX,
    request.week,
    request.timezone,
    request.rdId ?? null,
  ] as const;
}

export function rdScheduleWeekApiPath(request: RdScheduleWeekRequest): string {
  const query = new URLSearchParams({ timezone: request.timezone });

  if (request.week) query.set("week", request.week);

  if (request.rdId) query.set("rdId", request.rdId);

  return `/api/rd-schedule/week?${query.toString()}`;
}

/**
 * The Week view shows only with `rd-schedule` on in the server's
 * /api/features answer and in the single-rail frame; loading or error reads as
 * off, and the classic frame never shows it.
 */
export function weekViewEnabled(
  features: Record<string, boolean> | undefined,
  frame: "classic" | "rail"
): boolean {
  return features?.[RD_SCHEDULE_FLAG] === true && frame === "rail";
}

// ---------------------------------------------------------------------------
// Time

function dateParts(date: string): { year: number; month: number; day: number } {
  const [year, month, day] = date.split("-").map(Number);

  return { year: year ?? 1970, month: month ?? 1, day: day ?? 1 };
}

function zonedParts(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  return {
    date: `${String(read("year")).padStart(4, "0")}-${String(read("month")).padStart(2, "0")}-${String(read("day")).padStart(2, "0")}`,
    minutes: (read("hour") % 24) * 60 + read("minute"),
  };
}

/** Minutes since midnight of `date` in `timeZone`, clamped to that day. */
export function minutesOnDate(instant: string, date: string, timeZone: string): number {
  const parts = zonedParts(new Date(instant), timeZone);

  if (parts.date < date) return 0;

  if (parts.date > date) return 24 * 60;

  return parts.minutes;
}

/** The instant a wall-clock `HH:MM` on `date` is in `timeZone`. */
export function wallClockInstant(date: string, hhmm: string, timeZone: string): string {
  const { year, month, day } = dateParts(date);
  const [hour, minute] = hhmm.split(":").map(Number);
  const wall = Date.UTC(year, month - 1, day, hour ?? 0, minute ?? 0);
  let guess = wall;

  // Two passes settle the offset across a daylight-saving change.
  for (let pass = 0; pass < 2; pass += 1) {
    const shown = zonedParts(new Date(guess), timeZone);
    const shownWall = Date.UTC(
      dateParts(shown.date).year,
      dateParts(shown.date).month - 1,
      dateParts(shown.date).day,
      Math.floor(shown.minutes / 60),
      shown.minutes % 60
    );
    guess += wall - shownWall;
  }

  return new Date(guess).toISOString();
}

function rangeMinutes(
  range: { start: string; end: string },
  date: string,
  fromZone: string,
  toZone: string
): { start: number; end: number } {
  if (fromZone === toZone) {
    return { start: clockMinutes(range.start), end: clockMinutes(range.end) };
  }

  return {
    start: minutesOnDate(wallClockInstant(date, range.start, fromZone), date, toZone),
    end: minutesOnDate(wallClockInstant(date, range.end, fromZone), date, toZone),
  };
}

function clockMinutes(hhmm: string): number {
  const [hour, minute] = hhmm.split(":").map(Number);

  return (hour ?? 0) * 60 + (minute ?? 0);
}

/** "11:00", or "11:00 AM" with `meridiem`; the grid prints the bare form. */
export function clockLabel(totalMinutes: number, meridiem: boolean): string {
  const hour = Math.floor(totalMinutes / 60) % 24;
  const minute = totalMinutes % 60;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const base = `${hour12}:${String(minute).padStart(2, "0")}`;

  return meridiem ? `${base} ${hour < 12 ? "AM" : "PM"}` : base;
}

/** "11 AM", "12 PM": the gutter's hour labels. */
export function hourLabel(hour: number): string {
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;

  return `${hour12} ${hour % 24 < 12 ? "AM" : "PM"}`;
}

export function weekdayShort(date: string): string {
  const { year, month, day } = dateParts(date);

  return WEEKDAY_SHORT[(new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7]!;
}

/** The week header: "Oct 26 to 30, 2026", "Oct 26 to Nov 1, 2026". */
export function weekTitle(
  days: readonly { date: string }[],
  visible: readonly string[]
): string {
  const first = visible[0] ?? days[0]?.date;
  const last = visible[visible.length - 1] ?? days[days.length - 1]?.date;

  if (!first || !last) return "";

  const a = dateParts(first);
  const b = dateParts(last);
  const left = `${MONTH_SHORT[a.month - 1]} ${a.day}`;
  const right =
    a.month === b.month ? `${b.day}` : `${MONTH_SHORT[b.month - 1]} ${b.day}`;

  return a.year === b.year
    ? `${left} to ${right}, ${b.year}`
    : `${left}, ${a.year} to ${MONTH_SHORT[b.month - 1]} ${b.day}, ${b.year}`;
}

/** Hours with quarter fractions, as the canvas prints them: 14½, ¾, 22. */
export function hoursText(hours: number): string {
  const whole = Math.floor(hours);
  const quarter = Math.round((hours - whole) * 4);

  if (quarter === 4) return String(whole + 1);

  const fraction = QUARTERS[quarter] ?? "";

  return whole === 0 && fraction ? fraction : `${whole}${fraction}`;
}

export function minutesText(minutes: number): string {
  return hoursText(minutes / 60);
}

// ---------------------------------------------------------------------------
// Status looks

export type BlockTone =
  | "upcoming"
  | "done"
  | "cancelled"
  | "rescheduled"
  | "late_cancel"
  | "no_show"
  | "break"
  | "project";

/** Theme status pairs and tokens only; never the canvas's hex values. */
export const BLOCK_TONE_CLASS: Record<BlockTone, string> = {
  upcoming:
    "border border-transparent bg-schedule-upcoming text-foreground shadow-[inset_3px_0_0_var(--color-schedule-upcoming-edge)]",
  done: "border border-transparent bg-success-soft text-foreground",
  cancelled: "border border-transparent bg-warning-soft text-foreground",
  rescheduled: "border border-dashed border-border-strong bg-muted text-muted-strong",
  late_cancel:
    "border border-transparent bg-danger-soft text-foreground shadow-[inset_3px_0_0_var(--color-danger)]",
  // The no-show's full danger border, not the late cancel's inset edge, tells them apart.
  no_show:
    "border-2 border-danger bg-[color-mix(in_srgb,var(--color-danger)_17%,var(--color-danger-soft))] text-foreground",
  break: "border border-transparent bg-muted text-muted-strong",
  project: "border border-transparent bg-secondary text-foreground",
};

/** Breaks and personal time are hatched, so they never read as unavailable time. */
export const BREAK_HATCH =
  "repeating-linear-gradient(135deg, var(--muted) 0 5px, var(--secondary) 5px 6px)";

const CALENDAR_TONE: Record<RdScheduleCalendarStatus, BlockTone> = {
  upcoming: "upcoming",
  done: "done",
  cancelled: "cancelled",
  rescheduled: "rescheduled",
  late_cancel: "late_cancel",
  no_show: "no_show",
};

const STATUS_NAME: Record<RdScheduleCalendarStatus, string> = {
  upcoming: "Upcoming",
  done: "Done",
  cancelled: "Cancelled",
  rescheduled: "Rescheduled",
  late_cancel: "Late cancel",
  no_show: "No-show",
};

/** The statuses the "Show missed, cancelled and rescheduled" checkbox hides. */
const MISSED: ReadonlySet<RdScheduleCalendarStatus> = new Set([
  "cancelled",
  "rescheduled",
  "late_cancel",
  "no_show",
]);

export function isMissedStatus(status: RdScheduleCalendarStatus): boolean {
  return MISSED.has(status);
}

export function struckName(status: RdScheduleCalendarStatus): boolean {
  return status === "cancelled" || status === "rescheduled";
}

/**
 * "Late cancel, allowed, no fee"; "No-show, fee applies"; "Cancelled". The fee
 * is the rule's answer, never a recorded charge: nothing is charged in v1.
 */
export function statusNote(appointment: RdScheduleAppointment): string {
  const { calendarStatus: status, fee } = appointment;
  const name = STATUS_NAME[status];

  if (status === "upcoming" || status === "done") return "";

  if (status === "rescheduled" || status === "cancelled" || !fee) return name;

  const charge = fee.decision === "charge" ? "fee applies" : "no fee";

  if (status === "late_cancel") {
    return `${name}, ${fee.allowed ? "allowed" : "not allowed"}, ${charge}`;
  }

  return `${name}, ${charge}`;
}

// ---------------------------------------------------------------------------
// The grid

export interface WeekColumn {
  date: string;
  weekday: string;
  dayOfMonth: number;
  isToday: boolean;
  /** "3¼ h", or "" while the RD has no hours set. */
  hoursLabel: string;
  /** Open (bookable) ranges in the grid's zone, in minutes. */
  open: { start: number; end: number }[];
}

export interface WeekBlockLayout {
  key: string;
  /** The appointment id for a visit; null for the RD's own time. */
  appointmentId: string | null;
  kind: "visit" | "break" | "project" | "personal";
  tone: BlockTone;
  dayIndex: number;
  startMinutes: number;
  endMinutes: number;
  topPx: number;
  heightPx: number;
  /** Side-by-side columns for overlaps: this block's column and the count. */
  column: number;
  columns: number;
  title: string;
  /** The short Healthie appointment-type label; "" for the RD's own time. */
  typeLabel: string;
  tags: ("INITIAL" | "S CODE")[];
  note: string;
  tall: boolean;
  struck: boolean;
  recurring: boolean;
  noNextVisit: boolean;
  missed: boolean;
  ariaLabel: string;
  /** NO-917: where the visit's copy in Healthie stands; null for time that is not a visit. */
  mirrorState: "pending" | "failed" | null;
  /** The stored block behind the RD's own time, so Remove can name it (NO-877); null for visits and saved breaks. */
  ownBlock: RdScheduleBlock | null;
}

export interface WeekGridLayout {
  startHour: number;
  endHour: number;
  hourLabels: { hour: number; label: string; topPx: number }[];
  heightPx: number;
  columns: WeekColumn[];
  blocks: WeekBlockLayout[];
  /** Where the now line sits, as a column and a pixel offset; null off-week. */
  now: { dayIndex: number; topPx: number; label: string } | null;
  title: string;
}

export interface WeekGridOptions {
  showMissed: boolean;
}

/** Calendar weekends show only when the RD works them or has something on them. */
function visibleDayIndexes(
  days: readonly RdScheduleDay[],
  withContent: ReadonlySet<string>
): number[] {
  return days.flatMap((day, index) => {
    if (index < 5) return [index];

    const works = (day.availability?.week.length ?? 0) > 0;

    return works || withContent.has(day.date) ? [index] : [];
  });
}

export function buildWeekGrid(
  week: RdScheduleWeek,
  options: WeekGridOptions
): WeekGridLayout {
  const zone = week.timezone;
  const availabilityZone = week.availabilityTimeZone ?? zone;
  const dateIndex = new Map(week.days.map((day, index) => [day.date, index]));
  const appointments = week.appointments.filter(
    (appointment) => options.showMissed || !isMissedStatus(appointment.calendarStatus)
  );
  const placed = [
    ...appointments.map((appointment) => visitBlock(week, appointment, dateIndex)),
    ...week.blocks.map((block) => ownTimeBlock(week, block, dateIndex)),
    ...availabilityBreaks(week, availabilityZone),
  ].filter((block): block is PlacedBlock => block !== null);
  const openByDay = week.days.map((day) =>
    (day.availability?.week ?? []).map((range) =>
      rangeMinutes(range, day.date, availabilityZone, zone)
    )
  );
  const withContent = new Set(placed.map((block) => week.days[block.dayIndex]!.date));
  const visible = visibleDayIndexes(week.days, withContent);
  const range = hourRange(placed, openByDay, visible);
  const columns = visible.map((index) => columnFor(week, index, openByDay[index]!));
  const dayIndexes = new Map(visible.map((dayIndex, column) => [dayIndex, column]));
  const blocks = layoutBlocks(
    placed.filter((block) => dayIndexes.has(block.dayIndex)),
    dayIndexes,
    range.startHour
  );

  return {
    startHour: range.startHour,
    endHour: range.endHour,
    hourLabels: hourLabels(range),
    heightPx: (range.endHour - range.startHour) * WEEK_HOUR_PX,
    columns,
    blocks,
    now: nowMarker(week, dayIndexes, range.startHour),
    title: weekTitle(
      week.days,
      visible.map((index) => week.days[index]!.date)
    ),
  };
}

interface PlacedBlock extends Omit<
  WeekBlockLayout,
  "topPx" | "heightPx" | "column" | "columns" | "dayIndex"
> {
  dayIndex: number;
}

/**
 * The short label a Week block shows for a Healthie appointment-type name.
 * Matches the names the codebase uses ("Initial", "Initial visit", "Follow-up",
 * "Follow Up", "Follow up", "Follow-up 45"/"Follow-up 60", "S code"); any other
 * name shows trimmed as it is, and a blank one shows nothing.
 */
export function appointmentTypeLabel(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim().replace(/\s+/g, " ");
  const plain = trimmed.toLowerCase();

  if (/^s[\s-]?code\b|\bsupplemental\b/.test(plain)) return "S code";

  if (/\binitial\b|\bassessment\b/.test(plain)) return "Initial";

  if (/\bfollow[\s-]?up\b/.test(plain)) return "Follow-up";

  // The server's placeholder when a visit has no type says nothing on a block.
  if (plain === "appointment") return "";

  return trimmed;
}

function visitBlock(
  week: RdScheduleWeek,
  appointment: RdScheduleAppointment,
  dateIndex: ReadonlyMap<string, number>
): PlacedBlock | null {
  const date = zonedParts(new Date(appointment.startsAt), week.timezone).date;
  const dayIndex = dateIndex.get(date);

  if (dayIndex === undefined) return null;

  const start = minutesOnDate(appointment.startsAt, date, week.timezone);
  const end = Math.max(
    start + 15,
    minutesOnDate(appointment.endsAt, date, week.timezone)
  );
  const tags = appointment.tags.map((tag) =>
    tag === "initial" ? "INITIAL" : "S CODE"
  ) as ("INITIAL" | "S CODE")[];
  const status = appointment.calendarStatus;
  const note = statusNote(appointment);
  const name = appointment.client.name ?? "Client";
  const recurring = appointment.recurring !== null;
  const noNext = appointment.noNextVisit;
  const mirror =
    appointment.mirrorState === "pending" || appointment.mirrorState === "failed"
      ? appointment.mirrorState
      : null;

  return {
    key: appointment.id,
    appointmentId: appointment.id,
    kind: "visit",
    tone: CALENDAR_TONE[status],
    dayIndex,
    startMinutes: start,
    endMinutes: end,
    title: name,
    typeLabel: appointmentTypeLabel(appointment.appointmentType.name),
    tags,
    note,
    tall: ((end - start) / 60) * WEEK_HOUR_PX >= TALL_BLOCK_PX,
    struck: struckName(status),
    recurring,
    noNextVisit: noNext,
    missed: isMissedStatus(status),
    ariaLabel: [
      name,
      appointment.appointmentType.name,
      STATUS_NAME[status],
      `${weekdayShort(date)} ${clockLabel(start, true)}`,
      ...(recurring ? ["recurring"] : []),
      ...(noNext ? ["no next visit booked"] : []),
      ...(note && note !== STATUS_NAME[status] ? [note] : []),
      ...(mirror === "pending" ? [MIRROR_PENDING_TEXT] : []),
      ...(mirror === "failed" ? [MIRROR_FAILED_TEXT] : []),
    ].join(", "),
    mirrorState: mirror,
    ownBlock: null,
  };
}

const OWN_TIME_TITLE: Record<RdScheduleBlock["kind"], string> = {
  break: "Break",
  project: "Project",
  personal: "Personal",
};

/** The Week grid's own marker, so a dialog that closes can hand focus back to it. */
export const WEEK_GRID_SELECTOR = "[data-week-grid]";

/** A visit's block in the Week grid, the control that opened its details. */
export function appointmentBlockSelector(appointmentId: string): string {
  const quoted = appointmentId.replace(/["\\]/g, "\\$&");

  return `[data-block-key][data-appointment-id="${quoted}"]`;
}

/** The stable key a stored blocked-time occurrence takes in the grid (`data-block-key`). */
export function ownBlockKey(block: RdScheduleBlock): string {
  return `${block.id}:${block.startsAt}`;
}

function ownTimeBlock(
  week: RdScheduleWeek,
  block: RdScheduleBlock,
  dateIndex: ReadonlyMap<string, number>
): PlacedBlock | null {
  const date = zonedParts(new Date(block.startsAt), week.timezone).date;
  const dayIndex = dateIndex.get(date);

  if (dayIndex === undefined) return null;

  const start = minutesOnDate(block.startsAt, date, week.timezone);
  const end = Math.max(start + 15, minutesOnDate(block.endsAt, date, week.timezone));

  return heldBlock({
    key: ownBlockKey(block),
    ownBlock: block,
    kind: block.kind,
    dayIndex,
    date,
    start,
    end,
    // A personal block's text is the RD's own; only a project's reaches the page.
    note: block.kind === "project" ? (block.description ?? "") : "",
  });
}

/** A break that starts between 11 AM and 2 PM reads "Lunch"; any other is a "Break". */
export function breakTitle(startMinutes: number): string {
  return startMinutes >= 11 * 60 && startMinutes < 14 * 60 ? "Lunch" : "Break";
}

function heldBlock(input: {
  key: string;
  ownBlock?: RdScheduleBlock;
  kind: "break" | "project" | "personal";
  dayIndex: number;
  date: string;
  start: number;
  end: number;
  note: string;
}): PlacedBlock {
  const title = OWN_TIME_TITLE[input.kind];

  return {
    key: input.key,
    appointmentId: null,
    kind: input.kind,
    // Personal time is busy, not project hours: it takes the hatched break look.
    tone: input.kind === "project" ? "project" : "break",
    dayIndex: input.dayIndex,
    startMinutes: input.start,
    endMinutes: input.end,
    title: input.kind === "break" ? breakTitle(input.start) : title,
    typeLabel: "",
    tags: [],
    note: input.note,
    tall: ((input.end - input.start) / 60) * WEEK_HOUR_PX >= TALL_BLOCK_PX,
    struck: false,
    recurring: false,
    noNextVisit: false,
    missed: false,
    ariaLabel: `${title}, ${weekdayShort(input.date)} ${clockLabel(input.start, true)}`,
    mirrorState: null,
    ownBlock: input.ownBlock ?? null,
  };
}

/** The RD's saved breaks, unless a stored break block already covers them. */
function availabilityBreaks(
  week: RdScheduleWeek,
  availabilityZone: string
): PlacedBlock[] {
  const covered = week.blocks.filter((block) => block.kind === "break");

  return week.days.flatMap((day, dayIndex) =>
    (day.availability?.breaks ?? []).flatMap((range) => {
      const { start, end } = rangeMinutes(
        range,
        day.date,
        availabilityZone,
        week.timezone
      );
      const overlaps = covered.some((block) => {
        const blockStart = minutesOnDate(block.startsAt, day.date, week.timezone);
        const blockEnd = minutesOnDate(block.endsAt, day.date, week.timezone);

        return blockStart < end && blockEnd > start;
      });

      if (overlaps || end <= start) return [];

      return [
        heldBlock({
          key: `availability-break:${day.date}:${range.start}`,
          kind: "break",
          dayIndex,
          date: day.date,
          start,
          end,
          note: "",
        }),
      ];
    })
  );
}

function hourRange(
  placed: readonly PlacedBlock[],
  openByDay: readonly { start: number; end: number }[][],
  visible: readonly number[]
): { startHour: number; endHour: number } {
  let earliest = WEEK_MIN_START_HOUR * 60;
  let latest = WEEK_MIN_END_HOUR * 60;

  for (const block of placed) {
    earliest = Math.min(earliest, block.startMinutes);
    latest = Math.max(latest, block.endMinutes);
  }

  for (const index of visible) {
    for (const range of openByDay[index] ?? []) {
      earliest = Math.min(earliest, range.start);
      latest = Math.max(latest, range.end);
    }
  }

  return {
    startHour: Math.floor(earliest / 60),
    endHour: Math.min(24, Math.ceil(latest / 60)),
  };
}

function hourLabels(range: { startHour: number; endHour: number }) {
  return Array.from({ length: range.endHour - range.startHour }, (_, offset) => {
    const hour = range.startHour + offset;

    return { hour, label: hourLabel(hour), topPx: offset * WEEK_HOUR_PX };
  });
}

function columnFor(
  week: RdScheduleWeek,
  index: number,
  open: { start: number; end: number }[]
): WeekColumn {
  const day = week.days[index]!;
  const done = day.clientFacingMinutes.done + day.clientFacingMinutes.booked;

  return {
    date: day.date,
    weekday: weekdayShort(day.date),
    dayOfMonth: dateParts(day.date).day,
    isToday: day.isToday,
    hoursLabel: day.availability === null ? "" : `${minutesText(done)} h`,
    open,
  };
}

/** Overlapping blocks share a day side by side; the rest take the full width. */
function layoutBlocks(
  blocks: readonly PlacedBlock[],
  dayIndexes: ReadonlyMap<number, number>,
  startHour: number
): WeekBlockLayout[] {
  const out: WeekBlockLayout[] = [];
  const byDay = new Map<number, PlacedBlock[]>();

  for (const block of blocks) {
    byDay.set(block.dayIndex, [...(byDay.get(block.dayIndex) ?? []), block]);
  }

  for (const [dayIndex, list] of byDay) {
    const sorted = [...list].sort(
      (a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes
    );

    for (const cluster of overlapClusters(sorted)) {
      const ends: number[] = [];
      const columnOf = new Map<PlacedBlock, number>();

      for (const block of cluster) {
        let column = ends.findIndex((end) => end <= block.startMinutes);

        if (column === -1) column = ends.length;

        ends[column] = block.endMinutes;
        columnOf.set(block, column);
      }

      for (const block of cluster) {
        out.push({
          ...block,
          dayIndex: dayIndexes.get(dayIndex)!,
          topPx: ((block.startMinutes - startHour * 60) / 60) * WEEK_HOUR_PX + 1,
          heightPx: ((block.endMinutes - block.startMinutes) / 60) * WEEK_HOUR_PX - 2,
          column: columnOf.get(block)!,
          columns: ends.length,
        });
      }
    }
  }

  return out.sort((a, b) => a.dayIndex - b.dayIndex || a.startMinutes - b.startMinutes);
}

function overlapClusters(sorted: readonly PlacedBlock[]): PlacedBlock[][] {
  const clusters: PlacedBlock[][] = [];
  let end = -1;

  for (const block of sorted) {
    const current = clusters[clusters.length - 1];

    if (current && block.startMinutes < end) {
      current.push(block);
      end = Math.max(end, block.endMinutes);
    } else {
      clusters.push([block]);
      end = block.endMinutes;
    }
  }

  return clusters;
}

function nowMarker(
  week: RdScheduleWeek,
  dayIndexes: ReadonlyMap<number, number>,
  startHour: number
): WeekGridLayout["now"] {
  const todayIndex = week.days.findIndex((day) => day.isToday);
  const column = dayIndexes.get(todayIndex);

  if (todayIndex === -1 || column === undefined) return null;

  const minutes = minutesOnDate(week.now, week.days[todayIndex]!.date, week.timezone);

  return {
    dayIndex: column,
    topPx: ((minutes - startHour * 60) / 60) * WEEK_HOUR_PX,
    label: `Now, ${clockLabel(minutes, true)}`,
  };
}

// ---------------------------------------------------------------------------
// Keyboard: arrow keys move within the grid

export type GridArrow = "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight";

/** The visit block an arrow key moves to from `currentKey`, or null to stay. */
export function arrowTarget(
  blocks: readonly WeekBlockLayout[],
  currentKey: string,
  arrow: GridArrow
): string | null {
  const focusable = blocks.filter((block) => block.appointmentId !== null);
  const current = focusable.find((block) => block.key === currentKey);

  if (!current) return null;

  const sameDay = focusable.filter((block) => block.dayIndex === current.dayIndex);
  const position = sameDay.indexOf(current);

  if (arrow === "ArrowDown") return sameDay[position + 1]?.key ?? null;

  if (arrow === "ArrowUp") return sameDay[position - 1]?.key ?? null;

  const step = arrow === "ArrowRight" ? 1 : -1;
  const days = [...new Set(focusable.map((block) => block.dayIndex))].sort(
    (a, b) => a - b
  );
  const nextDay = days[days.indexOf(current.dayIndex) + step];

  if (nextDay === undefined) return null;

  const candidates = focusable.filter((block) => block.dayIndex === nextDay);
  const nearest = [...candidates].sort(
    (a, b) =>
      Math.abs(a.startMinutes - current.startMinutes) -
      Math.abs(b.startMinutes - current.startMinutes)
  )[0];

  return nearest?.key ?? null;
}

// ---------------------------------------------------------------------------
// The summary row

/** `to_book` and `short` are the unbooked track; `over` is plain text past the bar. */
export interface HoursLegendItem {
  kind: "done" | "booked" | "to_book" | "short" | "over";
  text: string;
}

export type HoursCell =
  /** No weekly hours saved: the tally still counts, there is just nothing to track against. */
  | { kind: "no_hours"; big: string; hasContract: boolean }
  /** NO-907: hours are saved but start after this week. */
  | { kind: "starts_later"; big: string; week: string; label: string }
  | { kind: "no_contract"; big: string }
  | {
      kind: "bar";
      big: string;
      of: string;
      doneShare: number;
      bookedShare: number;
      /** The bold status word before the legend: only "Contract hit"; null otherwise. */
      word: string | null;
      /** Done, booked, then the track or the overage; items that round to 0 are left out. */
      legend: HoursLegendItem[];
      /** Plain facts after the legend: open hours left, the proration (no full stops). */
      note: string | null;
      hit: boolean;
      ariaLabel: string;
      ariaNow: number;
      ariaMax: number;
    };

function dayRunLabel(dates: readonly string[]): string {
  const names = dates.map(weekdayShort);
  const indexes = names.map((name) =>
    (WEEKDAY_SHORT as readonly string[]).indexOf(name)
  );
  const contiguous = indexes.every(
    (index, at) => at === 0 || index === indexes[at - 1]! + 1
  );

  if (names.length === 1) return names[0]!;

  if (contiguous) return `${names[0]} to ${names[names.length - 1]}`;

  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "Mon, Nov 2": the day the saved hours start. */
export function hoursStartLabel(date: string): string {
  const { month, day } = dateParts(date);

  return `${weekdayShort(date)}, ${MONTH_SHORT[month - 1]} ${day}`;
}

/** Quarter hours, the precision the Week prints (see hoursText). */
function quarter(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

/**
 * Quarter hours, rounded down: the counted hours never claim time not yet
 * worked or booked. The server's targets are multiples of 15, so the floor of
 * the total reaches the target exactly when the total does (contract_hit).
 */
function quarterDown(minutes: number): number {
  return Math.floor(minutes / 15) * 15;
}

/** "hour" for a quarter, half or one hour; "hours" otherwise, decided from the printed text. */
function hoursWord(minutes: number): string {
  return minutes <= 60 ? "hour" : "hours";
}

export function hoursCell(
  hours: RdScheduleHours,
  hoursStartsOn: string | null = null
): HoursCell {
  const total = hours.doneMinutes + hours.bookedMinutes;

  if (hours.state === "no_hours_set") {
    return hoursStartsOn === null
      ? {
          kind: "no_hours",
          big: minutesText(total),
          // The server checks no_hours_set first but still computes the target.
          hasContract: hours.targetMinutes !== null,
        }
      : {
          kind: "starts_later",
          big: minutesText(total),
          week: hoursStartsOn,
          label: hoursStartLabel(hoursStartsOn),
        };
  }

  if (hours.state === "no_contract" || hours.targetMinutes === null) {
    return { kind: "no_contract", big: minutesText(total) };
  }

  // The big number, the legend and the bar are all built from these quarters,
  // so they agree: done plus booked is the big number, whatever the minutes.
  // Counted hours round down (a total 1 to 7 minutes short of the target must
  // not print "22 of 22" without "Contract hit"); the target is already a
  // multiple of 15.
  const target = quarter(hours.targetMinutes);
  const counted = quarterDown(total);
  const done = Math.min(quarterDown(hours.doneMinutes), counted);
  const booked = counted - done;
  const prorated =
    hours.daysOff.length > 0 &&
    hours.usualTargetMinutes !== null &&
    hours.usualTargetMinutes !== hours.targetMinutes;
  const proration = prorated
    ? `Usually ${minutesText(hours.usualTargetMinutes!)}, prorated for ${dayRunLabel(hours.daysOff)} off`
    : null;
  const copy = hoursCopy(hours, { done, booked, target });
  const note = [copy.note, proration].filter(Boolean).join(" · ");
  // Past the contract the bar is the whole total, so it stays full and matches the legend.
  const scale = Math.max(target, counted);
  const share = (minutes: number): number =>
    scale === 0 ? 0 : (100 * minutes) / scale;
  const of = `of ${minutesText(target)} ${hoursWord(target)}`;

  return {
    kind: "bar",
    big: minutesText(counted),
    of: prorated ? `${of} this week` : of,
    doneShare: share(done),
    bookedShare: share(booked),
    word: copy.word,
    legend: copy.legend,
    note: note === "" ? null : note,
    hit: hours.state === "contract_hit",
    ariaLabel: `${minutesText(counted)} ${of}, ${copy.status}: ${[
      ...copy.legend
        .filter((item) => item.kind === "done" || item.kind === "booked")
        .map((item) => item.text),
      ...(copy.note ? [copy.note] : []),
    ].join(", ")}`,
    ariaNow: counted / 60,
    ariaMax: target / 60,
  };
}

/** Everything in quarter minutes: the legend's done and booked add up to the big number. */
function hoursCopy(
  hours: RdScheduleHours,
  parts: { done: number; booked: number; target: number }
): {
  word: string | null;
  legend: HoursLegendItem[];
  note: string | null;
  /** The state in words, for the progress bar's spoken value. */
  status: string;
} {
  const { done, booked, target } = parts;
  const legend: HoursLegendItem[] = [];

  const add = (kind: HoursLegendItem["kind"], minutes: number, label: string): void => {
    // Rounded to quarters already, so 0 minutes is exactly the "0" that would print.
    if (minutes > 0) legend.push({ kind, text: `${minutesText(minutes)} ${label}` });
  };

  add("done", done, "done");
  add("booked", booked, "booked");

  if (hours.state === "contract_hit") {
    const over = done + booked - target;

    add("over", over, "over");

    return {
      word: "Contract hit",
      legend,
      note: null,
      status: over > 0 ? `contract hit, ${minutesText(over)} over` : "contract hit",
    };
  }

  const togo = Math.max(0, target - done - booked);

  if (hours.state === "short") {
    add("short", togo, "short");

    const open = hours.openMinutesLeft === null ? 0 : quarter(hours.openMinutesLeft);

    return {
      word: null,
      legend,
      note:
        open > 0 ? `${minutesText(open)} open ${hoursWord(open)} left this week` : null,
      status: togo > 0 ? `${minutesText(togo)} short` : "short",
    };
  }

  add("to_book", togo, "to book");

  return {
    word: null,
    legend,
    note: null,
    status: togo > 0 ? `${minutesText(togo)} to book` : "to book",
  };
}

export interface WeekSummary {
  hours: HoursCell;
  projectHours: string;
  /** The supporting line under Project hours. */
  projectNote: string;
  sCodes: string;
  /** The supporting line under S codes. */
  sCodeNote: string;
  noNextVisit: {
    count: number;
    text: string;
    hasAny: boolean;
    /** One per Client, in the order their latest visit this week starts. */
    clients: NoNextClient[];
  };
}

/** A Client with no next visit and the visit the summary opens for them. */
export interface NoNextClient {
  patientId: string;
  name: string;
  appointmentId: string;
}

/**
 * The Clients flagged `noNextVisit` this week, from the same appointments the
 * grid's !! marks come from. The server flags every visit of a Client together
 * and counts Clients, so the list length is the count. Each Client opens their
 * latest visit of the week that was not cancelled, missed or rescheduled (any
 * latest visit if all were), in the order those visits start. A Client with no
 * name is "Client (Wed)" by that visit's day, so two of them can be told apart.
 */
export function noNextClients(
  appointments: readonly RdScheduleAppointment[],
  timezone = "UTC"
): NoNextClient[] {
  // Missed covers cancelled, late cancel, no-show and rescheduled.
  const kept = (appointment: RdScheduleAppointment): boolean =>
    !isMissedStatus(appointment.calendarStatus);
  const later = (a: RdScheduleAppointment, b: RdScheduleAppointment): boolean =>
    Date.parse(a.startsAt) > Date.parse(b.startsAt);
  // A kept visit beats a missed one; between two of a kind, the later one wins.
  const replaces = (appointment: RdScheduleAppointment, seen: RdScheduleAppointment) =>
    kept(appointment) === kept(seen) ? later(appointment, seen) : kept(appointment);
  const chosen = new Map<string, RdScheduleAppointment>();

  for (const appointment of appointments) {
    if (!appointment.noNextVisit) continue;

    const seen = chosen.get(appointment.client.patientId);

    if (!seen || replaces(appointment, seen)) {
      chosen.set(appointment.client.patientId, appointment);
    }
  }

  return [...chosen.values()]
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
    .map((appointment) => ({
      patientId: appointment.client.patientId,
      name:
        appointment.client.name ??
        `Client (${weekdayShort(zonedParts(new Date(appointment.startsAt), timezone).date)})`,
      appointmentId: appointment.id,
    }));
}

export const S_CODE_NOTE = "Booked or done this week";

export function projectNote(blockCount: number): string {
  if (blockCount === 0) return "No project time this week";

  return `${blockCount} project ${blockCount === 1 ? "block" : "blocks"}`;
}

export function weekSummary(week: RdScheduleWeek): WeekSummary {
  const clients = noNextClients(week.appointments, week.timezone);
  const noNext = clients.length;

  return {
    hours: hoursCell(week.hours, week.hoursStartsOn),
    projectHours: minutesText(week.totals.projectMinutes),
    projectNote: projectNote(week.totals.projectBlockCount),
    sCodes: String(week.totals.sCodeCount),
    // Describes what the number counts, so it reads the same at 0.
    sCodeNote: S_CODE_NOTE,
    noNextVisit: {
      count: noNext,
      text: String(noNext),
      hasAny: noNext > 0,
      clients,
    },
  };
}

function addDays(date: string, days: number): string {
  const { year, month, day } = dateParts(date);

  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** The header's title for a requested week (Monday to Friday) before data. */
export function requestedWeekTitle(weekStart: string): string {
  const dates = [0, 1, 2, 3, 4].map((offset) => addDays(weekStart, offset));

  return weekTitle(
    dates.map((date) => ({ date })),
    dates
  );
}

// ---------------------------------------------------------------------------
// The key's remembered state

export const WEEK_KEY_STORAGE_PREFIX = "alwaysai:week-key-open:";

export function weekKeyStorageKey(userId: string): string {
  return `${WEEK_KEY_STORAGE_PREFIX}${userId}`;
}

/** Off unless the stored value is exactly "1". */
export function parseStoredWeekKeyOpen(stored: string | null): boolean {
  return stored === "1";
}

export function serializeWeekKeyOpen(open: boolean): string {
  return open ? "1" : "0";
}
